import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api, { apiError } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Upload } from "lucide-react";
import { PropertyPnLPanel } from "../components/PropertyLedger";
import { BillsPanel, PodSyncCard } from "../components/BillsPanel";
import { toast } from "sonner";

const EXPENSE_CATEGORIES = [
  "utility_gas", "utility_electric", "utility_water", "council_tax",
  "internet", "insurance", "cleaning_supplies", "maintenance", "mortgage",
  "accountant", "platform_fees", "subscription", "office", "other",
  "refuse_collection", "tv_licence", "rent",
];

const prettyCat = (c) => (c || "").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

function TabBar({ value, onChange, items }) {
  return (
    <div className="flex gap-1 border-b border-slate-200 mb-4" data-testid="finance-tabs">
      {items.map((it) => (
        <button
          key={it.value}
          data-testid={`finance-tab-${it.value}`}
          onClick={() => onChange(it.value)}
          className={`px-4 py-2 text-sm transition border-b-2 -mb-px ${value === it.value ? "border-teal-700 text-teal-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"}`}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

export default function FinancePage() {
  const [tab, setTab] = useState("expenses");
  const [props, setProps] = useState([]);
  useEffect(() => { api.get("/properties").then((r) => setProps(r.data || [])).catch(() => {}); }, []);

  return (
    <Layout title="Finance" subtitle="Expenses, income, imports">
      <TabBar
        value={tab}
        onChange={setTab}
        items={[
          { value: "expenses", label: "Expenses" },
          { value: "income", label: "Income" },
          { value: "property", label: "By property" },
          { value: "bills", label: "Bills" },
          { value: "import", label: "CSV import" },
        ]}
      />
      {tab === "expenses" && <ExpensesPanel props={props} />}
      {tab === "income" && <IncomePanel props={props} />}
      {tab === "property" && <PropertyPnLPanel />}
      {tab === "bills" && <BillsPanel props={props} />}
      {tab === "import" && <ImportPanel props={props} onDone={() => setTab("expenses")} />}
    </Layout>
  );
}

function ExpensesPanel({ props }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ rows: [], grand_total: 0 });
  const [filter, setFilter] = useState({ property_id: "", category: "", date_from: "", date_to: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ property_id: "", category: "utility_electric", description: "", amount: "", vat_amount: "", date: new Date().toISOString().slice(0, 10), vendor: "" });

  const load = async () => {
    const params = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => { if (v) params.set(k, v); });
    const [list, sum] = await Promise.all([
      api.get(`/expenses?${params.toString()}`),
      api.get(`/expenses/summary?${params.toString()}`),
    ]);
    setRows(list.data || []);
    setSummary(sum.data || { rows: [], grand_total: 0 });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.property_id, filter.category, filter.date_from, filter.date_to]);

  const save = async () => {
    try {
      await api.post("/expenses", {
        property_id: form.property_id || null,
        category: form.category,
        description: form.description,
        amount: parseFloat(form.amount) || 0,
        vat_amount: form.vat_amount ? parseFloat(form.vat_amount) : null,
        date: form.date,
        vendor: form.vendor,
      });
      toast.success("Expense added");
      setOpen(false);
      await load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    await api.delete(`/expenses/${id}`);
    toast.success("Deleted");
    await load();
  };

  const propName = (id) => id ? (props.find((p) => p.id === id)?.name || "—") : "Portfolio";

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <div className="ns-label mb-1">Property</div>
          <Select value={filter.property_id || "any"} onValueChange={(v) => setFilter((f) => ({ ...f, property_id: v === "any" ? "" : v }))}>
            <SelectTrigger className="w-56 bg-white" data-testid="finance-filter-property"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All</SelectItem>
              <SelectItem value="portfolio">Portfolio only</SelectItem>
              {props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="ns-label mb-1">Category</div>
          <Select value={filter.category || "any"} onValueChange={(v) => setFilter((f) => ({ ...f, category: v === "any" ? "" : v }))}>
            <SelectTrigger className="w-56 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All</SelectItem>
              {EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{prettyCat(c)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="ns-label mb-1">From</div>
          <Input type="date" value={filter.date_from} onChange={(e) => setFilter((f) => ({ ...f, date_from: e.target.value }))} className="bg-white w-40" />
        </div>
        <div>
          <div className="ns-label mb-1">To</div>
          <Input type="date" value={filter.date_to} onChange={(e) => setFilter((f) => ({ ...f, date_to: e.target.value }))} className="bg-white w-40" />
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-full ns-btn-primary ml-auto" data-testid="finance-add-expense-btn">
          <Plus className="w-4 h-4 mr-2" /> Add expense
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {summary.rows.slice(0, 7).map((r) => (
          <div key={r.category} className="ns-card p-3" data-testid={`finance-summary-${r.category}`}>
            <div className="ns-label">{prettyCat(r.category)}</div>
            <div className="font-serif text-lg">£{r.total.toFixed(2)}</div>
            <div className="text-[11px] text-slate-500">{r.count} entries</div>
          </div>
        ))}
        <div className="ns-card p-3 !bg-slate-900 !border-slate-900 text-white" data-testid="finance-grand-total">
          <div className="ns-label text-slate-300">Grand total</div>
          <div className="font-serif text-xl">£{(summary.grand_total || 0).toFixed(2)}</div>
        </div>
      </div>

      <table className="w-full text-sm bg-white rounded-xl border border-slate-100" data-testid="finance-expenses-table">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
            <th className="py-2 px-3">Date</th><th className="py-2 px-3">Property</th><th className="py-2 px-3">Category</th><th className="py-2 px-3">Vendor</th><th className="py-2 px-3 text-right">Amount</th><th className="py-2 px-3"></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={6} className="py-6 text-center text-slate-400 italic">No expenses — add one or import from CSV</td></tr>
          ) : rows.map((r) => (
            <tr key={r.id} data-testid={`finance-expense-${r.id}`} className="border-b border-slate-50 hover:bg-slate-50/60">
              <td className="py-2 px-3 text-xs text-slate-500">{r.date}</td>
              <td className="py-2 px-3 text-xs">{propName(r.property_id)}</td>
              <td className="py-2 px-3 text-xs">{prettyCat(r.category)}</td>
              <td className="py-2 px-3 text-xs text-slate-600">{r.vendor}</td>
              <td className="py-2 px-3 text-sm text-right font-mono">£{r.amount.toFixed(2)}</td>
              <td className="py-2 px-3 text-right">
                <button onClick={() => del(r.id)} className="text-slate-300 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add expense</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><div className="ns-label mb-1">Date</div><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div><div className="ns-label mb-1">Amount (£)</div><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="finance-expense-amount" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><div className="ns-label mb-1">Category</div><Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{prettyCat(c)}</SelectItem>)}</SelectContent></Select></div>
              <div><div className="ns-label mb-1">Property</div><Select value={form.property_id || "portfolio"} onValueChange={(v) => setForm({ ...form, property_id: v === "portfolio" ? "" : v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="portfolio">Portfolio</SelectItem>{props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><div className="ns-label mb-1">Vendor</div><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="British Gas, Welsh Water…" /></div>
            <div><div className="ns-label mb-1">Description</div><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><div className="ns-label mb-1">VAT (£, optional)</div><Input type="number" step="0.01" value={form.vat_amount} onChange={(e) => setForm({ ...form, vat_amount: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="ns-btn-primary" data-testid="finance-expense-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function IncomePanel({ props }) {
  const [data, setData] = useState({ rows: [], totals: { manual: 0, stripe: 0, direct_booking: 0, total: 0 } });
  const [range, setRange] = useState({ date_from: "", date_to: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ property_id: "", description: "", amount: "", date: new Date().toISOString().slice(0, 10) });

  const load = async () => {
    const params = new URLSearchParams();
    if (range.date_from) params.set("date_from", range.date_from);
    if (range.date_to) params.set("date_to", range.date_to);
    const r = await api.get(`/income/consolidated?${params.toString()}`);
    setData(r.data || { rows: [], totals: {} });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range.date_from, range.date_to]);

  const save = async () => {
    try {
      await api.post("/income", { ...form, amount: parseFloat(form.amount) || 0, source: "manual", property_id: form.property_id || null });
      toast.success("Income recorded");
      setOpen(false);
      await load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const propName = (id) => id ? (props.find((p) => p.id === id)?.name || "—") : "Portfolio";
  const sourceTone = (s) => s === "stripe" ? "bg-indigo-50 text-indigo-800 border-indigo-100" : s === "direct_booking" ? "bg-emerald-50 text-emerald-800 border-emerald-100" : "bg-slate-50 text-slate-700 border-slate-100";

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <div><div className="ns-label mb-1">From</div><Input type="date" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} className="bg-white w-40" /></div>
        <div><div className="ns-label mb-1">To</div><Input type="date" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} className="bg-white w-40" /></div>
        <Button onClick={() => setOpen(true)} className="rounded-full ns-btn-primary ml-auto" data-testid="finance-add-income-btn">
          <Plus className="w-4 h-4 mr-2" /> Add income
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {["manual", "stripe", "direct_booking", "recurring", "ota_booking"].map((k) => (
          <div key={k} className="ns-card p-3" data-testid={`finance-income-total-${k}`}>
            <div className="ns-label">{prettyCat(k)}</div>
            <div className="font-serif text-lg">£{(data.totals[k] || 0).toFixed(2)}</div>
          </div>
        ))}
        <div className="ns-card p-3 !bg-emerald-900 !border-emerald-900 text-white" data-testid="finance-income-total">
          <div className="ns-label text-emerald-200">Total income</div>
          <div className="font-serif text-xl">£{(data.totals.total || 0).toFixed(2)}</div>
        </div>
      </div>
      <table className="w-full text-sm bg-white rounded-xl border border-slate-100" data-testid="finance-income-table">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
            <th className="py-2 px-3">Date</th><th className="py-2 px-3">Source</th><th className="py-2 px-3">Property</th><th className="py-2 px-3">Description</th><th className="py-2 px-3 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.length === 0 ? (
            <tr><td colSpan={5} className="py-6 text-center text-slate-400 italic">No income in this window</td></tr>
          ) : data.rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-50">
              <td className="py-2 px-3 text-xs text-slate-500">{r.date}</td>
              <td className="py-2 px-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] border ${sourceTone(r.source)}`}>{prettyCat(r.source)}</span></td>
              <td className="py-2 px-3 text-xs">{propName(r.property_id)}</td>
              <td className="py-2 px-3 text-xs text-slate-600">{r.description}</td>
              <td className="py-2 px-3 text-sm text-right font-mono">£{(r.amount || 0).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Record income</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><div className="ns-label mb-1">Date</div><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div><div className="ns-label mb-1">Amount (£)</div><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="finance-income-amount" /></div>
            </div>
            <div><div className="ns-label mb-1">Property</div><Select value={form.property_id || "portfolio"} onValueChange={(v) => setForm({ ...form, property_id: v === "portfolio" ? "" : v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="portfolio">Portfolio</SelectItem>{props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
            <div><div className="ns-label mb-1">Description</div><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Monthly rent, cash deposit…" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="ns-btn-primary" data-testid="finance-income-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ImportPanel({ props, onDone }) {
  const [csv, setCsv] = useState("");
  const [propId, setPropId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [createMissing, setCreateMissing] = useState(true);
  const isPod = /property_name/i.test(csv.split("\n")[0] || "") && /lease_end/i.test(csv.split("\n")[0] || "");
  const loadFile = (f) => { if (f) f.text().then(setCsv); };
  const run = async () => {
    setBusy(true);
    try {
      if (isPod) {
        const r = await api.post("/pod-secure/import", { csv, create_missing: createMissing });
        setResult({ pod: true, ...r.data, inserted: r.data.matched.length, skipped: r.data.unmatched.map((n) => `no CRM property matched "${n}"`) });
        toast.success(`${r.data.matched.length} properties updated · ${r.data.recurring_expenses_created} recurring expenses booked`);
        return;
      }
      const r = await api.post("/expenses/import", { csv, property_id: propId || null });
      setResult(r.data);
      if (r.data.inserted > 0) toast.success(`${r.data.inserted} expenses imported`);
      if (r.data.skipped.length > 0) toast.warning(`${r.data.skipped.length} rows skipped — see details`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4 max-w-3xl">
      <PodSyncCard />
      <div className="ns-card p-4">
        <div className="font-serif text-lg mb-2">Import pod-secure CSV</div>
        <p className="text-xs text-slate-500 mb-3">
          Paste or upload the pod-secure property export (access codes, lease dates, utility accounts, certificates are detected automatically), or an expense ledger. Expense ledger columns (case-insensitive, any order):
          <code className="ml-1 text-slate-700">date, category, amount, vat, vendor, description, property, notes</code>.
          Property can be the unit name or UUID. Blank property goes to the portfolio ledger.
        </p>
        <div className="mb-3">
          <div className="ns-label mb-1">Default property if row doesn't specify</div>
          <Select value={propId || "portfolio"} onValueChange={(v) => setPropId(v === "portfolio" ? "" : v)}>
            <SelectTrigger className="w-full md:w-72"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="portfolio">Portfolio (no property)</SelectItem>
              {props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3 mb-3">
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer rounded-full border border-slate-200 px-3 py-1.5 hover:bg-slate-50 transition-colors">
            <Upload className="w-3.5 h-3.5" /> Choose CSV file
            <input type="file" accept=".csv,text/csv" className="hidden" data-testid="finance-import-file" onChange={(e) => loadFile(e.target.files?.[0])} />
          </label>
          {isPod && <span className="text-[11px] px-2 py-0.5 rounded-full bg-teal-50 text-teal-800 border border-teal-100" data-testid="finance-import-pod-detected">Pod-secure property export detected</span>}
          {isPod && (
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <input type="checkbox" checked={createMissing} onChange={(e) => setCreateMissing(e.target.checked)} data-testid="finance-import-create-missing" />
              Create properties not found in CRM
            </label>
          )}
        </div>
        <Textarea
          rows={12}
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          placeholder="date,category,amount,vendor,description,property&#10;2026-01-14,utility_electric,142.30,Octopus,January bill,Riverside Loft 01"
          className="font-mono text-xs"
          data-testid="finance-import-csv"
        />
        <div className="flex items-center gap-2 mt-3">
          <Button onClick={run} disabled={busy || !csv.trim()} className="ns-btn-primary" data-testid="finance-import-run">
            <Upload className="w-4 h-4 mr-2" /> {busy ? "Importing…" : "Import"}
          </Button>
          {result && <span className="text-xs text-slate-500" data-testid="finance-import-result">{result.inserted} {result.pod ? "properties updated" : "inserted"} · {result.skipped.length} skipped</span>}
        </div>
      </div>
      {result?.pod && (
        <div className="ns-card p-4" data-testid="finance-import-pod-summary">
          <div className="ns-label mb-2">Imported into CRM</div>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-400"><th className="py-1">Pod-secure</th><th>CRM property</th><th>Access</th><th>Utilities</th><th>Certs</th><th>Lease ends</th></tr></thead>
            <tbody>{result.matched.map((m) => (
              <tr key={m.property_id} className="border-t border-slate-50"><td className="py-1">{m.pod_name}</td><td className="text-slate-600">{m.property_name}</td><td>{m.access_points}</td><td>{m.utilities}</td><td>{m.certificates}</td><td>{m.lease_end || "—"}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {result && result.skipped.length > 0 && (
        <div className="ns-card p-4">
          <div className="ns-label mb-2">Skipped rows</div>
          <ul className="text-xs text-slate-600 space-y-1 max-h-40 overflow-auto">
            {result.skipped.map((s, i) => <li key={i} className="font-mono">· {s}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
